import type { LngLat } from '../shared/types';
import { angleDiff } from './geo';

/**
 * 高速道路の分岐点での車線案内（描画に依存しない）。
 * データは public/data/highway-lanes.json（scripts/build-highway-lanes.mjs で OSM から作る）。
 *
 * OSM の turn:lanes（車線ごとの進行方向）は一部の分岐にしか無いので、無いところは
 * 「分岐の手前の車線数」と「分かれた先の道の車線数」から、左右の端に割り当てて推定する。
 */

export type LaneArrow = 'left' | 'slight_left' | 'through' | 'slight_right' | 'right';

export interface Lane {
  arrows: LaneArrow[];
  /** 進むべき車線 */
  recommended: boolean;
}

export interface LaneGuide {
  /** 左から順 */
  lanes: Lane[];
  /** 分かれた先の行き先（「茅ヶ崎・圏央道」）。無ければ undefined */
  toward?: string;
  /** turn:lanes から作ったか（false なら車線数からの推定） */
  tagged: boolean;
}

export interface JunctionOut {
  bearing: number;
  lanes: number; // 0 = 不明
  link: boolean;
  destination: string;
}

export interface Junction {
  inBearing: number; // -1 = 不明
  inLanes: number; // 0 = 不明
  inTurn: string;
  outs: JunctionOut[];
}

/** JSON の1件: [経度, 緯度, 入る向き, 入る車線数, 入る turn:lanes, [[向き, 車線数, ランプか, 行き先], ...]] */
export type JunctionRow = [number, number, number, number, string, [number, number, number, string][]];

/** ルートの座標と突き合わせるためのキー（データもルートも小数6桁なので一致する） */
export const junctionKey = (p: LngLat) => `${p[0].toFixed(6)},${p[1].toFixed(6)}`;

export function parseJunctions(rows: JunctionRow[]): Map<string, Junction> {
  const m = new Map<string, Junction>();
  for (const [lng, lat, inBearing, inLanes, inTurn, outs] of rows) {
    m.set(junctionKey([lng, lat]), {
      inBearing,
      inLanes,
      inTurn,
      outs: outs.map(([bearing, lanes, link, destination]) => ({ bearing, lanes, link: !!link, destination })),
    });
  }
  return m;
}

const LEFTISH = new Set(['left', 'slight_left', 'sharp_left']);
const RIGHTISH = new Set(['right', 'slight_right', 'sharp_right']);

function arrowOfTag(v: string): LaneArrow {
  if (v === 'left' || v === 'sharp_left') return 'left';
  if (v === 'slight_left') return 'slight_left';
  if (v === 'right' || v === 'sharp_right') return 'right';
  if (v === 'slight_right') return 'slight_right';
  return 'through'; // through / none / 空 / merge_to_* など
}

/**
 * 分岐点の車線案内を作る。travelBearing はルートが分岐のあと進む向き。
 * どの出口にも近くない（ルートがこの分岐を使っていない）ときは null
 */
export function laneGuide(j: Junction, travelBearing: number): LaneGuide | null {
  const chosen = j.outs.reduce((best, o) =>
    Math.abs(angleDiff(travelBearing, o.bearing)) < Math.abs(angleDiff(travelBearing, best.bearing)) ? o : best,
  );
  if (Math.abs(angleDiff(travelBearing, chosen.bearing)) > 50) return null;

  const base = j.inBearing >= 0 ? j.inBearing : travelBearing;
  const rel = (o: JunctionOut) => angleDiff(base, o.bearing); // 負 = 左
  const sorted = [...j.outs].sort((a, b) => rel(a) - rel(b));
  const idx = sorted.indexOf(chosen);
  const toward = chosen.destination || undefined;

  const tagged = fromTurnLanes(j, sorted, idx);
  const lanes = tagged ?? fromLaneCounts(j, sorted, idx, rel) ?? [];
  // どの車線でもよいときは車線は出さない（行き先だけ）
  const useful = lanes.some((l) => !l.recommended) ? lanes : [];
  if (!useful.length && !toward) return null;
  return { lanes: useful, toward, tagged: !!tagged };
}

/**
 * turn:lanes があるとき。
 * - 選んだのが左の分かれ道で、左向きの車線がある → 左向きの車線
 * - 選んだのが右の分かれ道で、右向きの車線がある → 右向きの車線
 * - 選んだのがまっすぐ側（もう一方の分かれ道の向きだけタグがある）→ その向きではない車線
 * タグがこの分岐を表していない（向きが合わない）ときは null にして推定に任せる
 */
function fromTurnLanes(j: Junction, sorted: JunctionOut[], idx: number): Lane[] | null {
  if (!j.inTurn || !j.inTurn.includes('|') || sorted.length < 2) return null;
  const tags = j.inTurn.split('|').map((l) => (l ? l.split(';') : ['none']));
  const has = (set: Set<string>, vs: string[]) => vs.some((v) => set.has(v));
  const anyLeft = tags.some((vs) => has(LEFTISH, vs));
  const anyRight = tags.some((vs) => has(RIGHTISH, vs));
  const leftBranch = idx === 0;
  const rightBranch = idx === sorted.length - 1;

  let rec: boolean[];
  if (leftBranch && anyLeft) rec = tags.map((vs) => has(LEFTISH, vs));
  else if (rightBranch && anyRight) rec = tags.map((vs) => has(RIGHTISH, vs));
  else if (rightBranch && anyLeft && !anyRight) rec = tags.map((vs) => !has(LEFTISH, vs) || vs.includes('through'));
  else if (leftBranch && anyRight && !anyLeft) rec = tags.map((vs) => !has(RIGHTISH, vs) || vs.includes('through'));
  else return null;
  if (!rec.some(Boolean)) return null;
  return tags.map((vs, i) => ({ arrows: [...new Set(vs.map(arrowOfTag))], recommended: rec[i] }));
}

/**
 * 車線数から推定する。分かれた先の道を左から並べ、左端の道には左から、右端の道には右から車線を割り当てる。
 * 割り当ての重なり（どちらにも行ける車線）はどちらにも数え、余った車線はいちばんまっすぐな道に付ける
 */
function fromLaneCounts(j: Junction, sorted: JunctionOut[], idx: number, rel: (o: JunctionOut) => number): Lane[] | null {
  const counts = sorted.map((o) => o.lanes || (o.link ? 1 : 2));
  const n = Math.min(8, j.inLanes || counts.reduce((a, b) => a + b, 0));
  if (n < 2) return null;
  const owners: number[][] = Array.from({ length: n }, () => []);
  const last = sorted.length - 1;
  counts[0] = Math.min(counts[0], n);
  counts[last] = Math.min(counts[last], n);
  for (let k = 0; k < counts[0]; k++) owners[k].push(0);
  for (let k = n - counts[last]; k < n; k++) owners[k].push(last);
  // 3本以上に分かれるときの真ん中の道は、残りの真ん中に付ける
  for (let m = 1; m < last; m++) {
    const free = owners.map((o, k) => (o.length ? -1 : k)).filter((k) => k >= 0);
    for (const k of free.slice(0, counts[m])) owners[k].push(m);
  }
  // 「まっすぐ」の道: 本線（ランプでない道）を優先し、その中で向きの変わりがいちばん小さいもの。
  // 分岐の直後は道がほぼ平行なので、角度だけで決めると出口も「直進」になってしまう
  const pool = sorted.some((o) => !o.link) ? sorted.filter((o) => !o.link) : sorted;
  const straightest = sorted.indexOf(pool.reduce((b, o) => (Math.abs(rel(o)) < Math.abs(rel(b)) ? o : b)));
  for (const o of owners) if (!o.length) o.push(straightest);
  // 矢印は並び順で決める（まっすぐの道より左なら左向き、右なら右向き。大きく曲がるときだけ「←」「→」）
  const arrowOf = (i: number): LaneArrow =>
    i === straightest ? 'through' : i < straightest ? (rel(sorted[i]) < -60 ? 'left' : 'slight_left') : rel(sorted[i]) > 60 ? 'right' : 'slight_right';
  return owners.map((o) => ({
    // 進むべき車線には進む向きだけを描く（本線と共有の車線でも「↖↑」と重ねない）
    arrows: o.includes(idx) ? [arrowOf(idx)] : [...new Set(o.map(arrowOf))].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b)),
    recommended: o.includes(idx),
  }));
}

const ORDER: LaneArrow[] = ['left', 'slight_left', 'through', 'slight_right', 'right'];

/** 読み上げに添える一言（「左側の車線を進んでください」）。全車線どれでもよいときは空 */
export function laneHint(g: LaneGuide): string {
  const rec = g.lanes.map((l) => l.recommended);
  const count = rec.filter(Boolean).length;
  if (!count || count === rec.length) return '';
  const first = rec.indexOf(true);
  const lastRec = rec.lastIndexOf(true);
  if (first === 0) return count === 1 ? '左側の車線を進んでください' : `左側${count}車線を進んでください`;
  if (lastRec === rec.length - 1) return count === 1 ? '右側の車線を進んでください' : `右側${count}車線を進んでください`;
  return '中央の車線を進んでください';
}

/** 読み上げ・案内文用に行き先を短くする（最初の2つまで） */
export const shortToward = (toward: string) => toward.split('・').slice(0, 2).join('・');
